/**
 * SharePanel — composer-side affordance + active-share banner.
 *
 * iter135 frontend integration. Renders inside ConversationsV2Page:
 *
 *   ┌─ chat header ─────────────────────────────────┐
 *   │ {ShareBanner}  ← when a Share is live         │
 *   └───────────────────────────────────────────────┘
 *   │ messages …                                     │
 *   ├─ composer ────────────────────────────────────┤
 *   │ [📎] [📺 ShareButton] [text input ] [Send]    │
 *   └───────────────────────────────────────────────┘
 *
 * Behaviour:
 *   - ShareButton: not sharing → opens Presenter modal that captures
 *     screen + calls ``startShare`` + opens the gateway WS.
 *   - ShareBanner: shows when ``getShareStatus`` returns a live row.
 *     For the current viewer (non-Presenter, in PCS), exposes a
 *     "Join" button that calls ``joinShare``.
 *   - PresenterModal: shows the local screen-capture preview and
 *     a "Stop sharing" button that ends the Share + closes the modal.
 *
 * Frame relay is not implemented in this iteration — see
 * ``/app/frontend/src/lib/screen-share/client.js`` for the rationale.
 * The panel surfaces the auth lifecycle correctly and renders the
 * Presenter's local preview; viewers see a "Coming soon" placeholder
 * until SFU credentials are provisioned.
 */
import { useEffect, useRef, useState } from "react";
import { Monitor, X as XIcon, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  joinShare,
  leaveShare,
  getShareStatus,
  openSignalingWs,
} from "@/lib/screen-share/client";
import {
  viewerConnect,
} from "@/lib/screen-share/livekitTransport";
import { useScreenSharePresenter } from "@/lib/screen-share/PresenterContext";
import { useScreenShareViewer } from "@/lib/screen-share/ViewerContext";
import { useT } from "@/lib/i18n";

// ── Composer toolbar button ────────────────────────────────────

export function ShareButton({ conversationId, archived }) {
  const { startPresenting, current } = useScreenSharePresenter();
  const [busy, setBusy] = useState(false);
  // Disable when the user is already presenting (in this conv OR
  // any other) — the gateway also enforces D10 server-side.
  const alreadyPresenting = !!current;
  const onClick = async () => {
    if (archived || busy || alreadyPresenting) return;
    setBusy(true);
    try {
      await startPresenting(conversationId);
    } catch {
      // startPresenting already toasts on real errors and swallows
      // user-cancel — nothing to do here.
    } finally {
      setBusy(false);
    }
  };
  const title = archived
    ? "Conversation closed"
    : alreadyPresenting
    ? "You're already presenting in another conversation"
    : "Share your screen";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={archived || busy || alreadyPresenting}
      data-testid="conv-share-btn"
      title={title}
      className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 disabled:opacity-40"
    >
      {busy ? (
        <CircleNotch size={18} weight="bold" className="animate-spin" />
      ) : (
        <Monitor size={18} weight="bold" />
      )}
    </button>
  );
}

// ── Presenter live preview modal ───────────────────────────────
//
// REMOVED in iter135.6 — the Presenter modal now lives in
// ``PresenterContext`` so it survives route navigation. Re-exported
// here as a no-op so any legacy imports compile without surprises.

export function PresenterModal() { return null; }

// ── Banner shown to non-Presenters when a Share is live ────────

export function ShareBanner({ conversationId, share, currentUserId, currentUserType, onJoined }) {
  const t = useT();
  const [joining, setJoining] = useState(false);
  const { startViewing, current: activeView } = useScreenShareViewer();
  if (!share || share.state !== "active") return null;
  const isMyShare =
    String(share.presenter?.user_id) === String(currentUserId) &&
    share.presenter?.user_type === currentUserType;
  // Same user opened a second tab / window: hide the "Join" CTA but
  // surface a thin info pill so the duplicate tab isn't visually
  // empty. The actual presenter UI is rendered in the OTHER tab —
  // there's no useful screen-share state to project here.
  if (isMyShare) {
    return (
      <div
        className="shrink-0 px-3 py-1.5 flex items-center gap-2 text-[12px]"
        style={{
          background: "rgba(0, 168, 132, 0.08)",
          borderBottom: "1px solid rgba(0, 168, 132, 0.18)",
        }}
        data-testid="conv-share-banner-self"
      >
        <span
          className="inline-block w-2 h-2 rounded-full animate-pulse"
          style={{ background: "#00a884" }}
        />
        <Monitor size={14} weight="duotone" style={{ color: "#00a884" }} />
        <span className="text-slate-600">
          You're presenting your screen from another tab.
          <span className="text-slate-400 ml-1">·
            {" "}{share.viewer_count ?? 0}{" "}
            {(share.viewer_count ?? 0) === 1 ? "viewer" : "viewers"}</span>
        </span>
      </div>
    );
  }
  const isCurrentlyViewing = !!activeView && activeView.share_id === share.share_id;
  const onJoin = async () => {
    if (isCurrentlyViewing) {
      // Idempotent: if we're already viewing this share (maybe in
      // compact mode), do nothing — the overlay is already present.
      return;
    }
    setJoining(true);
    try {
      const joined = await startViewing(conversationId);
      // Kept for backwards-compat with the page's useShareState —
      // ``onJoined`` historically populated ``viewerSession`` so
      // the legacy ViewerModal would render. The global
      // ViewerProvider renders its own overlay, so this callback is
      // now a no-op when it fires. We still fire it for any
      // parent that wants to know "a viewer session began".
      if (joined) onJoined?.(joined);
    } finally {
      setJoining(false);
    }
  };
  return (
    <div
      className="shrink-0 px-3 py-2 flex items-center justify-between"
      style={{
        background: "rgba(0, 168, 132, 0.12)",
        borderBottom: "1px solid rgba(0, 168, 132, 0.25)",
      }}
      data-testid="conv-share-banner"
    >
      <div className="flex items-center gap-2 text-[12px] text-slate-700">
        <span
          className="inline-block w-2 h-2 rounded-full animate-pulse"
          style={{ background: "#00a884" }}
        />
        <Monitor size={14} weight="duotone" style={{ color: "#00a884" }} />
        <span>
          <strong>{t("convv2.share_in_progress")}</strong> · {share.viewer_count}
          {" "}{share.viewer_count === 1 ? "viewer" : "viewers"}
        </span>
      </div>
      <button
        onClick={onJoin}
        disabled={joining || isCurrentlyViewing}
        data-testid="conv-share-join-btn"
        className="h-7 px-3 rounded-full text-white text-[11px] font-bold uppercase tracking-wider hover:brightness-95 disabled:opacity-50"
        style={{ background: isCurrentlyViewing ? "#06875b" : "#00a884" }}
      >
        {joining ? "Joining…" : isCurrentlyViewing ? "Viewing" : "Join"}
      </button>
    </div>
  );
}

// ── Viewer modal — iter135.13: moved to global ViewerProvider ─────
// Kept as a no-op stub for any legacy import path.

export function ViewerModal() { return null; }

// ── Hook — wires it all together for the parent page ──────────

export function useShareState(conversationId) {
  // ``current`` from the global presenter context replaces the
  // page-local ``presenterSession`` so navigation away from
  // /conversations-v2 doesn't tear down the share. Viewer state
  // stays local because viewing is meaningful only while the chat
  // pane is mounted.
  const presenter = useScreenSharePresenter();
  const presenterSession = presenter.current &&
    String(presenter.current.conversationId) === String(conversationId)
      ? presenter.current
      : null;

  const [share, setShare] = useState(null);
  const [viewerSession, setViewerSession] = useState(null);
  // Polled status — every 10 s when no live session is active.
  // While in a session our optimistic state takes over, so the
  // poll is paused.
  useEffect(() => {
    if (!conversationId || presenterSession || viewerSession) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const s = await getShareStatus(conversationId);
        if (!cancelled) setShare(s);
      } catch { /* swallow — banner just won't show */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [conversationId, presenterSession, viewerSession]);
  return {
    share,
    presenterSession,
    viewerSession,
    // Kept for backwards-compat with the page wiring; the global
    // provider is the authority now, so these are mostly no-ops.
    onShareStart: () => {},
    onShareEnded: () => setShare(null),
    onJoined: (v) => setViewerSession(v),
    onLeft: () => setViewerSession(null),
  };
}
